<template>
  <td :colspan="headers.length" class="pa-8 expanded-row-bg">

    <!-- Section: Details -->
    <div class="text-h5 font-weight-light mb-6">
      {{ $t('reports.expand.title') }}
    </div>

    <div class="mb-10">
      <!-- IDs slot - caller provides what's relevant -->
      <slot name="ids" :item="item" />

      <!-- Best Rate -->
      <div v-if="!isPro" class="d-flex flex-wrap align-center mb-2">
        <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.bestRate') }}:</span>
        <span>{{ item.best_rate || '-' }}</span>
      </div>

      <!-- Client Rate (final rate with all spreads) -->
      <div class="d-flex flex-wrap align-center mb-2">
        <span class="input-title-text font-weight-bold mr-2">{{ clientRateLabel }}:</span>
        <span>{{ clientRate }}</span>
        <v-chip v-if="item.rfq_reconcil_sign && !isPro" data-testid="report-reconciled" class="px-2 ml-3" outlined x-small color="green"> <span class="white--text">{{ $t('reports.expand.reconciledDetails') }}</span> </v-chip>
        <div v-if="canReconcile && isBroker && !item.rfq_reconcil_sign && item.rfq_status === 'DEAL'">
          <v-btn @click="emitReconcile(item)" x-small color="primary" class="mx-3 elevation-0" >
            {{ $t('instructions.reconcile') }}
          </v-btn>
        </div>
      </div>

      <!-- Notional (BRL) - volume_brl is qty * client rate -->
      <div v-if="item.volume_brl != null" class="d-flex flex-wrap align-center mb-2">
        <span class="input-title-text font-weight-bold mr-2">{{ $t('quoteInfo.volumeCcyBrl') }}:</span>
        <span>{{ formatNotional(item.volume_brl) }}</span>
      </div>

      <!-- Revenue Breakdown for Broker - Their share, partner's share, and total -->
      <template v-if="isBroker && item.revenue_total != null">
        <div class="d-flex flex-wrap align-center mb-2">
          <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.revenueTotal') }}:</span>
          <span>{{  formatRevenue(item.revenue_total) }}</span>
        </div>
        <div class="d-flex flex-wrap align-center mb-2">
          <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.revenueBroker') }}:</span>
          <span>{{ formatRevenue(item.revenue_broker) }}</span>
        </div>
        <div class="d-flex flex-wrap align-center mb-2">
          <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.revenuePartner') }}:</span>
          <span>{{ formatRevenue(item.revenue_partner) }}</span>
        </div>
      </template>

      <!-- Revenue for Broker - When RFQ has no partner, display `revenue` as "Broker Revenue" -->
      <template v-else-if="isBroker && item.revenue != null">
        <div class="d-flex flex-wrap align-center mb-2">
          <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.revenueBroker') }}:</span>
          <span>{{ formatRevenue(item.revenue) }}</span>
        </div>
      </template>

      <!-- Revenue for Partner - Sees only their own share -->
      <template v-else-if="isPartner && item.revenue != null">
        <div class="d-flex flex-wrap align-center mb-2">
          <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.revenue') }}:</span>
          <span>{{ formatRevenue(item.revenue) }}</span>
        </div>
      </template>

      <!-- Side -->
      <div class="d-flex flex-wrap align-center mb-2">
        <span class="input-title-text font-weight-bold mr-2">{{ $t('reports.expand.side') }}:</span>
        <span :class="sideClass(sideValue)">
          {{ rfqSideLabel(sideValue) }}
        </span>
      </div>

      <!-- Status -->
      <div class="d-flex flex-wrap align-center mb-2">
        <span class="input-title-text font-weight-bold mr-2">{{ $t('quoteInfo.status') }}:</span>
        <span>{{ formatStatus(statusValue) }}</span>
      </div>
    </div>

    <!-- Section: Rates -->
    <template v-if="hasRates">
      <div class="text-h5 font-weight-light mb-5">
        {{ $t('reports.expand.rates.title') }}
      </div>

      <v-card elevation="0" outlined>
        <v-simple-table class="expanded-row-bg">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">{{ $t('quoteInfo.status') }}</th>
                <th class="text-left">{{ $t('quoteInfo.bankName') }}</th>
                <th v-if="!isPro" class="text-left">{{ $t('quoteInfo.bankRate') }}</th>

                <th v-if="isBroker" class="text-left" style="white-space: nowrap;">
                  {{ $t('quoteInfo.brokerMarkup') }}
                </th>
                <th v-else-if="!isPro" class="text-left">{{ $t('quoteInfo.spread') }}</th>

                <th v-if="isBroker && hasPartnerBroker" class="text-left" style="white-space: nowrap;">
                  {{ $t('quoteInfo.partnerSpread') }}
                </th>
                <th class="text-left">{{ clientRateLabel }}</th>
                <th class="text-left">{{ $t('quoteInfo.bankLastUpdate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="quote in quoteList" :key="quote.quote_id" data-testid="report-quote" :data-status="quoteStatus(quote)">
                <td>
                  <v-chip small :text-color="isAcceptedQuote(quote) ? 'primary' : '#BDBDBD'"
                    :color="isAcceptedQuote(quote) ? 'primaryTransp' : '#212121'">
                    <span data-testid="report-quote-status" class="font-weight-bold">{{ formatQuoteStatus(quote) }}</span>
                  </v-chip>
                </td>
                <!-- Bank Name -->
                <td>{{ quote.bank_label || '-' }}</td>
                <!--
                Note: For partners, quote_bank_rate "hides" the master spread by including
                the master broker markup on the backend to keep the math consistent
                (Client Rate = Bank Rate + Partner Spread).
                -->
                <td v-if="!isPro" data-testid="report-bank-rate">{{ quote.quote_bank_rate != null ? Number(quote.quote_bank_rate).toFixed(4) : '-' }}</td>

                <!-- Broker view: broker markup with units -->
                <td v-if="isBroker">{{ quote.broker_markup_str || '-' }}</td>
                <!-- Non-broker, non-pro: spread with units -->
                <td v-else-if="!isPro">{{ quote.spread_avg_spot || '-' }}</td>

                <!-- Partner spread - only when this RFQ came from a partner broker -->
                <td v-if="isBroker && hasPartnerBroker">
                  {{ quote.partner_markup_str || '-' }}
                </td>
                <!-- Client Rate (final) -->
                <td data-testid="report-client-rate">{{item.rfq_final_rate !== null ?  Number(quote.rfq_final_rate).toFixed(4) : '-' }}</td>
                <!-- Bank Last Update -->
                <td>{{ quote.quote_bank_time ? formatTimeAndDate(quote.quote_bank_time) : '-' }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </template>

  </td>
</template>

<script>
import {
  formatReportQuoteStatus,
  formatReportTimelineDate,
  getReportQuoteStatus,
  translateStatus,
} from '@/components/reports/reportHelpers.js'

export default {
  name: 'ReportsExpandedRow',
  props: {
    item: { type: Object, required: true },
    headers: { type: Array, required: true },
    isBroker: { type: Boolean, default: false },
    isPartner: { type: Boolean, default: false },
    isPro: { type: Boolean, default: false },
    sideField: { type: String, default: 'side' },
    statusField: { type: String, default: 'rfq_status' },
    canReconcile: { type: Boolean, default: true },
  },
  computed: {
    quoteList() {
      return this.item?.rfq_lastquote?.quote_list || []
    },
    hasRates() {
      return this.quoteList.length > 0
    },
    sideValue() {
      return this.item?.[this.sideField] || ''
    },
    statusValue() {
      return this.item?.[this.statusField] || ''
    },
    hasPartnerBroker() {
      // True when this RFQ was placed by one of the master broker's partners.
      // The backend sets item.partner to a {id, name} object in that case.
      return this.item?.partner != null
    },
    clientRateLabel() {
      return this.isPro ? this.$t('quoteInfo.rate') : this.$t('quoteInfo.clientRate')
    },
    /** Client Rate: current price (rfq_px reflects any reconciliation), row-level rfq_final_rate, or the winning quote */
    clientRate() {
      const currentRate = this.item?.rfq_final_rate
      if (currentRate != null) return Number(currentRate).toFixed(4)
      // Fallback: winning quote (should be the same thing)
      const winning = this.quoteList.find(q => q.deal)
      const qRate = winning?.rfq_final_rate
      if (qRate != null) return Number(qRate).toFixed(4)
      return '-'
    },
    /** Pre-reconciliation rate, only meaningful once item.rfq_reconcil_sign is present */
    originalRate() {
      const pxo = this.item?.rfq_reconcil_pxo
      return pxo != null ? Number(pxo).toFixed(4) : '-'
    },
  },
  methods: {
    sideClass(side) {
      return {
        'alive--text': side === 'BUY',
        'orangeColor--text': side === 'SELL',
      }
    },
    rfqSideLabel(side) {
      if (!side) return '-'
      if (side === 'SELL') return this.$t('quoteInfo.sell').toUpperCase()
      if (side === 'BUY') return this.$t('quoteInfo.buy').toUpperCase()
      return '-'
    },
    formatTimeAndDate(ts) {
      return formatReportTimelineDate(ts)
    },
    formatNotional(volume) {
      const n = Number(volume)
      if (!Number.isFinite(n)) return '-'
      return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatRevenue(revenue) {
      const n = Number(revenue)
      if (!Number.isFinite(n)) return '-'
      return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatStatus(status) {
      return translateStatus(status)
    },
    // The quote's outcome, for its `data-status` test marker.
    quoteStatus(quote) {
      return getReportQuoteStatus(quote, this.statusValue)
    },
    formatQuoteStatus(quote) {
      return formatReportQuoteStatus(quote, this.statusValue)
    },
    isAcceptedQuote(quote) {
      return getReportQuoteStatus(quote, this.statusValue) === 'accepted'
    },
    emitReconcile(item) {
      const newItem = {
        ...item,
        quote_list: this.quoteList,
      }
      this.$emit("reconcile", newItem)
    }
  },
}
</script>

<style scoped>
.expanded-row-bg {
  background: #121212 !important;
}
</style>
